import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  Alert,
} from '@mui/material';
import {
  ClipboardDocumentIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface ClientInfo {
  name?: string;
  age?: number;
  familyStatus?: string;
  currentCoverage?: string;
  mainConcerns?: string[];
  contactNumber?: string;
  email?: string;
}

interface FollowupDialogProps {
  open: boolean;
  onClose: () => void;
  clientInfo: ClientInfo;
  onSave: (
    clientInfo: ClientInfo,
    followupDate: string,
    followupTime: string,
    followupNotes: string,
  ) => Promise<string>;
}

export const FollowupDialog = ({
  open,
  onClose,
  clientInfo: initialClientInfo,
  onSave,
}: FollowupDialogProps) => {
  const [clientInfo, setClientInfo] = useState<ClientInfo>(initialClientInfo);
  const [followupDate, setFollowupDate] = useState<string>('');
  const [followupTime, setFollowupTime] = useState<string>('');
  const [followupNotes, setFollowupNotes] = useState<string>('');
  const [followupUrl, setFollowupUrl] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const url = await onSave(
        clientInfo,
        followupDate,
        followupTime,
        followupNotes,
      );
      setFollowupUrl(url);
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    if (followupUrl) {
      navigator.clipboard.writeText(followupUrl);
      toast.success('Link copied to clipboard');
    }
  };

  const updateClientInfo = (field: keyof ClientInfo, value: any) => {
    setClientInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>Schedule Follow-up Call</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Client Name"
              value={clientInfo.name || ''}
              onChange={(e) => updateClientInfo('name', e.target.value)}
              margin="normal"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Client Age"
              type="number"
              value={clientInfo.age || ''}
              onChange={(e) =>
                updateClientInfo('age', parseInt(e.target.value) || '')
              }
              margin="normal"
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Family Status"
              value={clientInfo.familyStatus || ''}
              onChange={(e) => updateClientInfo('familyStatus', e.target.value)}
              placeholder="e.g., Single, Married, With Children"
              margin="normal"
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Current Coverage"
              value={clientInfo.currentCoverage || ''}
              onChange={(e) =>
                updateClientInfo('currentCoverage', e.target.value)
              }
              placeholder="Current insurance details"
              margin="normal"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact Number"
              value={clientInfo.contactNumber || ''}
              onChange={(e) =>
                updateClientInfo('contactNumber', e.target.value)
              }
              margin="normal"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={clientInfo.email || ''}
              onChange={(e) => updateClientInfo('email', e.target.value)}
              margin="normal"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Follow-up Date"
              type="date"
              value={followupDate}
              onChange={(e) => setFollowupDate(e.target.value)}
              margin="normal"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Follow-up Time"
              type="time"
              value={followupTime}
              onChange={(e) => setFollowupTime(e.target.value)}
              margin="normal"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes for Follow-up"
              multiline
              rows={3}
              value={followupNotes}
              onChange={(e) => setFollowupNotes(e.target.value)}
              placeholder="Additional notes for the follow-up call"
              margin="normal"
              size="small"
            />
          </Grid>
        </Grid>

        {followupUrl && (
          <Box mt={2}>
            <Alert
              severity="success"
              action={
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={handleCopyLink}
                >
                  <ClipboardDocumentIcon width={20} height={20} />
                </IconButton>
              }
            >
              <Typography variant="body2">
                Follow-up link generated successfully
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}
              >
                {followupUrl}
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="error"
          startIcon={<DocumentCheckIcon width={20} height={20} />}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save & Generate Link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
